import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, useToast } from '@musa.tech/design-system';
import { WizardLayout } from '@/components/collects/sob-demanda/wizard';
import { useAuthContext } from '@/components/AuthProvider';
import {
  CONTRACTS_PAGE_SIZE,
  ContractSelectionStep,
} from '@/components/collects/sob-demanda/agreements/ContractSelectionStep';
import { RequestDetailsStep } from '@/components/collects/sob-demanda/agreements/RequestDetailsStep';
import {
  ServiceTypeToggle,
  type OnDemandService,
} from '@/components/collects/sob-demanda/agreements/ServiceTypeToggle';
import { BucketRequestWizard } from '@/components/collects/sob-demanda/agreements/BucketRequestWizard';
import {
  initialRequestForm,
  toCreateInput,
  validateRequestForm,
  type RequestFormErrors,
  type RequestFormState,
} from '@/components/collects/sob-demanda/agreements/requestForm';
import { useCreateOnDemandRequests, useOnDemandContracts } from '@/hooks/useAgreementsOnDemand';
import { AgreementsApiError, type OnDemandContract } from '@/integrations/agreements/client';
import { minAllowedDate } from '@/utils/onDemandRequestRules';
import { applyWeeklyFloor, describeWeeklyFloor, weeklyFirstAllowedDay } from '@/utils/weeklyDateFloor';
import { useNow } from '@/hooks/useNow';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toAgreementsErrorCode } from '@/utils/analyticsErrorCode';
import {
  ON_DEMAND_SERVICE_PARAM,
  onDemandButtonLabel,
} from '@/components/collects/sob-demanda/agreements/onDemandAnalytics';

const STEPS = [
  { id: 'contrato', label: 'Contrato' },
  { id: 'detalhes', label: 'Detalhes' },
];

const LIST_PATH = '/coletas?tab=sobdemanda';

/**
 * Nova solicitação sob demanda, no musa-agreements: coleta, sobre um contrato de coleta, ou
 * caçamba (colocação, retirada, troca), sobre um contrato de caçamba.
 */
const NewOnDemandRequest: React.FC = () => {
  const [service, setService] = useState<OnDemandService>(() =>
    new URLSearchParams(window.location.search).get('servico') === 'cacamba' ? 'bucket' : 'collect');
  const { trackButtonClick } = useAnalytics();

  const handleServiceChange = (next: OnDemandService) => {
    if (next === service) return;
    trackButtonClick({ buttonLabel: 'sobdemanda_trocar_servico', servico: ON_DEMAND_SERVICE_PARAM[next] });
    setService(next);
  };

  const serviceToggle = <ServiceTypeToggle value={service} onChange={handleServiceChange} />;

  return service === 'bucket'
    ? <BucketRequestWizard serviceToggle={serviceToggle} />
    : <CollectRequestWizard serviceToggle={serviceToggle} />;
};

/** Coleta sob demanda sobre um contrato de coleta. */
const CollectRequestWizard: React.FC<{ serviceToggle: React.ReactNode }> = ({ serviceToggle }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuthContext();
  const { trackButtonClick, trackError } = useAnalytics();
  const isInternal = !!profile?.is_internal;

  const [step, setStep] = useState(0);
  const [generatorId, setGeneratorId] = useState('');
  const [page, setPage] = useState(1);
  const [contract, setContract] = useState<OnDemandContract | null>(null);
  const [form, setForm] = useState<RequestFormState>(() => initialRequestForm());
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // o perfil chega depois do primeiro render; só preenche o que a pessoa ainda não digitou.
  // Depende dos campos, não do objeto: um perfil recriado a cada render entraria em laço.
  const profileName = profile?.user_name ?? '';
  const profilePhone = profile?.phone_number ?? '';
  const profileEmail = profile?.email ?? '';
  useEffect(() => {
    if (isInternal || (!profileName && !profilePhone && !profileEmail)) return;
    setForm((current) => ({
      ...current,
      requester: {
        name: current.requester.name || profileName,
        phone: current.requester.phone || profilePhone,
        email: current.requester.email || profileEmail,
      },
    }));
  }, [profileName, profilePhone, profileEmail, isInternal]);

  const contracts = useOnDemandContracts(
    { generatorId: generatorId ? Number(generatorId) : undefined, page, pageSize: CONTRACTS_PAGE_SIZE },
    { enabled: !isInternal || !!generatorId },
  );
  const createRequests = useCreateOnDemandRequests();

  // piso semanal: depois do corte de sexta, ou no fim de semana, só a partir de terça. O serviço
  // confere de novo; aqui ele só trava o calendário e explica
  const now = useNow();
  const weeklyFloor = useMemo(
    () => (contract ? weeklyFirstAllowedDay(contract.generator.name, now) : null),
    [contract, now],
  );
  const minDate = useMemo(
    () => applyWeeklyFloor(minAllowedDate(contract?.rule ?? null, now), weeklyFloor),
    [contract, now, weeklyFloor],
  );
  const weeklyNote = contract && weeklyFloor ? describeWeeklyFloor(contract.generator.name, weeklyFloor) : null;
  const errors: RequestFormErrors = useMemo(
    () => (contract
      ? validateRequestForm(form, {
        minDate,
        maxDate: contract.endsAt,
        // o aviso da antecedência erraria o motivo quando é o piso que manda
        minDateMessage: weeklyFloor === minDate && weeklyNote ? weeklyNote : undefined,
      })
      : {}),
    [contract, form, minDate, weeklyFloor, weeklyNote],
  );

  const handleGeneratorChange = (value: string) => {
    setGeneratorId(value);
    setPage(1);
    setContract(null);
  };

  const handleSubmit = async () => {
    if (!contract) return;

    setShowErrors(true);
    setSubmitError(null);
    if (Object.keys(errors).length) return;

    try {
      const created = await createRequests.mutateAsync({ contractId: contract.contractId, input: toCreateInput(form) });
      // conclusão do funil só com a solicitação confirmada pelo serviço
      trackButtonClick({ buttonLabel: onDemandButtonLabel('collect', 'criar_solicitacao_sucesso') });
      toast({
        variant: 'success',
        title: created.length === 1 ? 'Solicitação criada' : `${created.length} solicitações criadas`,
        description: 'O transportador vai aceitar ou recusar. Acompanhe o status na listagem.',
      });
      navigate(LIST_PATH);
    } catch (error) {
      trackError({ errorType: 'api_error', errorCode: toAgreementsErrorCode(error) });
      if (error instanceof AgreementsApiError && (error.isBusinessRule || error.isForbidden)) {
        setSubmitError(error.message);
        return;
      }
      toast({
        variant: 'error',
        title: 'A solicitação não foi criada',
        description: 'O serviço de coletas está indisponível no momento. Seus dados continuam aqui — tente de novo em instantes.',
      });
    }
  };

  const isSubmitting = createRequests.isPending;

  // sair pela primeira etapa é o abandono do formulário, como no fluxo antigo; o X não conta
  const handleCancel = () => {
    trackButtonClick({ buttonLabel: onDemandButtonLabel('collect', 'voltar_formulario') });
    navigate(LIST_PATH);
  };

  const handleContinue = () => {
    trackButtonClick({ buttonLabel: onDemandButtonLabel('collect', 'form_next_step'), form_step: 'contrato' });
    setStep(1);
  };

  const footer = step === 0 ? (
    <>
      <Button variant="outline" onClick={handleCancel}>
        <ChevronLeft className="mr-2 h-4 w-4" aria-hidden="true" />
        Cancelar
      </Button>
      <Button disabled={!contract} onClick={handleContinue} data-testid="on-demand-next">
        Continuar com o contrato
        <ChevronRight className="ml-2 h-4 w-4" aria-hidden="true" />
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" onClick={() => setStep(0)} disabled={isSubmitting}>
        <ChevronLeft className="mr-2 h-4 w-4" aria-hidden="true" />
        Voltar
      </Button>
      <Button
        onClick={handleSubmit}
        disabled={isSubmitting || isUploadingPhoto}
        data-testid="on-demand-submit"
      >
        {isSubmitting ? 'Criando solicitação...' : 'Criar solicitação'}
      </Button>
    </>
  );

  return (
    <WizardLayout steps={STEPS} currentIndex={step} onClose={() => navigate(LIST_PATH)} footer={footer}>
      {step === 0 || !contract ? (
        <ContractSelectionStep
          contracts={contracts.data?.items ?? []}
          total={contracts.data?.total ?? 0}
          page={page}
          onPageChange={setPage}
          isLoading={contracts.isLoading}
          isError={contracts.isError}
          generatorId={generatorId}
          onGeneratorChange={handleGeneratorChange}
          requiresGenerator={isInternal}
          selectedContractId={contract?.contractId ?? null}
          onSelect={(selected) => {
            setContract(selected);
            setSubmitError(null);
          }}
          headerSlot={serviceToggle}
        />
      ) : (
        <RequestDetailsStep
          contract={contract}
          form={form}
          onChange={setForm}
          minDate={minDate}
          weeklyNote={weeklyNote}
          errors={showErrors ? errors : {}}
          submitError={submitError}
          onPhotoUploadingChange={setIsUploadingPhoto}
        />
      )}
    </WizardLayout>
  );
};

export default NewOnDemandRequest;
